import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle2, Link2 } from "lucide-react";
import ConnectJotformButton from "@/components/ConnectJotformButton";
import { useProvider } from "@/contexts/ProviderContext";
import { logger } from "@/lib/logger";
import { fetchJotformPlanSettings } from "@/services/jotformPlanService";

interface JotformSettings {
  accountType: string;
  status: string;
  planSlug: string;
  matchedTier: {
    id: string;
    displayName: string;
    formLimit: number;
    retentionDays: number;
  };
}

const ConnectJotformPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshConnectionStatus } = useProvider();
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [matchedSettings, setMatchedSettings] = useState<JotformSettings | null>(null);

  const handleJotformConnected = async () => {
    setPlanError(null);
    setIsCheckingPlan(true);

    try {
      // Refresh connection status in ProviderContext and TanStack Query cache
      await refreshConnectionStatus("jotform");
      queryClient.setQueryData(['jotformConnectionStatus'], true);

      const data = await fetchJotformPlanSettings();

      // Check account status
      if (data.status !== "ACTIVE") {
        setPlanError(
          `Your Jotform account status is "${data.status}". FAB requires an active Jotform account. Please check your Jotform subscription and try again.`
        );
        setIsCheckingPlan(false);
        return;
      }

      setMatchedSettings(data as JotformSettings);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to check Jotform plan";
      logger.error("Error checking Jotform plan:", err);
      setPlanError(message);
    } finally {
      setIsCheckingPlan(false);
    }
  };

  const handleActivatePlan = () => {
    if (!matchedSettings) return;
    // Navigate to SelectTierPage which handles Stripe checkout
    navigate("/select-tier?action=activate");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-grow container mx-auto px-4 py-12 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <Link2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Connect Your Jotform Account</CardTitle>
            <CardDescription>
              FAB automatically matches your plan based on your Jotform account.
              Connect to get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Connect Jotform */}
            {!matchedSettings && !isCheckingPlan && (
              <ConnectJotformButton onConnected={handleJotformConnected} />
            )}

            {/* Loading: checking plan */}
            {isCheckingPlan && (
              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Checking your Jotform account plan...
                </p>
              </div>
            )}

            {/* Error state */}
            {planError && (
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{planError}</AlertDescription>
                </Alert>
                {!matchedSettings && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPlanError(null);
                      setMatchedSettings(null);
                    }}
                    className="w-full"
                  >
                    Try Again
                  </Button>
                )}
              </div>
            )}

            {/* Step 2: Show matched plan */}
            {matchedSettings && !planError && (
              <div className="space-y-6">
                <div className="rounded-lg border bg-muted/50 p-6 text-center space-y-3">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto" />
                  <h3 className="text-lg font-semibold">
                    Jotform {matchedSettings.matchedTier.displayName} Plan Detected
                  </h3>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>
                      <span className="font-medium text-foreground">
                        {matchedSettings.matchedTier.formLimit}
                      </span>{" "}
                      forms covered
                    </p>
                    <p>
                      <span className="font-medium text-foreground">
                        {matchedSettings.matchedTier.retentionDays}
                      </span>{" "}
                      days backup retention
                    </p>
                    <p>Automatic daily backups included</p>
                  </div>
                </div>

                <Button
                  onClick={handleActivatePlan}
                  className="w-full"
                  size="lg"
                >
                  Activate {matchedSettings.matchedTier.displayName} Plan
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Your FAB plan is matched to your Jotform subscription.
                  If you change Jotform plans, we'll notify you to update.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConnectJotformPage;
